import React, { useState, useEffect } from 'react';
import { DatePicker, Select } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { Dayjs } from 'dayjs';
import type { BillTimeRangeInput } from '../../graphql/types/settlement/settlementBillTypes';
import { BILL_TIMEZONE_OPTIONS } from '../../utils/timezoneFormatter';

dayjs.extend(utc);
dayjs.extend(timezone);

const { RangePicker } = DatePicker;

/** 将选择器返回的“墙钟”时间格式化为 YYYY-MM-DD HH:mm:ss（浏览器本地语义，与原先一致） */
function formatWallClockToTimeString(v: any): string {
  const d = v && typeof v.toDate === 'function' ? v.toDate() : new Date(v);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}:${s}`;
}

interface BillTimeRangePickerProps {
  value?: BillTimeRangeInput;
  onChange?: (value: BillTimeRangeInput | undefined) => void;
  /** 未传入 value 时使用的默认时区，如 America/Los_Angeles */
  defaultTimeZoneId?: string;
  /**
   * 仅选择日期：开始日自动为该时区 00:00:00，结束日自动为该时区 23:59:59（与 central/parcels 筛选项行为一致）
   */
  pickDateOnly?: boolean;
}

/** 账单时间范围仅支持 IANA 时区：Asia/Shanghai、America/Los_Angeles，根据所选时区转换时间 */
const TIMEZONE_OPTIONS = BILL_TIMEZONE_OPTIONS.map(({ value, label }) => ({ value, label }));

/**
 * 账单时间范围选择器
 * 使用 IANA 时区（Asia/Shanghai、America/Los_Angeles），根据所选时区转换时间后传给后端
 */
const DEFAULT_TZ = 'Asia/Shanghai';

/** 日历开始日 → 所选时区当日 00:00:00；结束日 → 所选时区当日 23:59:59（墙钟字符串传给后端） */
function buildDateOnlyBillTimeRange(start: Dayjs, end: Dayjs, tz: string): BillTimeRangeInput {
  const startDateStr = start.format('YYYY-MM-DD');
  const endDateStr = end.format('YYYY-MM-DD');
  const startWall = dayjs.tz(startDateStr, tz).startOf('day').format('YYYY-MM-DD HH:mm:ss');
  const endWall = dayjs.tz(endDateStr, tz).endOf('day').format('YYYY-MM-DD HH:mm:ss');
  return {
    startTime: startWall,
    endTime: endWall,
    timeZoneId: tz,
    timeFormat: 'yyyy-MM-dd HH:mm:ss'
  };
}

export const BillTimeRangePicker: React.FC<BillTimeRangePickerProps> = ({
  value,
  onChange,
  defaultTimeZoneId = DEFAULT_TZ,
  pickDateOnly = false
}) => {
  const [timeZone, setTimeZone] = useState<string>(value?.timeZoneId || defaultTimeZoneId);
  const [timeRange, setTimeRange] = useState<[Dayjs, Dayjs] | null>(null);

  useEffect(() => {
    const tz = value?.timeZoneId || defaultTimeZoneId;
    setTimeZone(tz);
    if (!value?.startTime || !value?.endTime) {
      setTimeRange(null);
      return;
    }
    const start = dayjs.tz(value.startTime, tz);
    const end = dayjs.tz(value.endTime, tz);
    if (!start.isValid() || !end.isValid()) {
      setTimeRange(null);
      return;
    }
    setTimeRange([start, end]);
  }, [value, defaultTimeZoneId]);

  /** 按所选时区语义：将用户选择的时间作为该时区的本地时间字符串传给后端，不做“时刻→时区格式化”转换 */
  const buildTimeRangeInput = (start: any, end: any, tz: string): BillTimeRangeInput => ({
    startTime: formatWallClockToTimeString(start),
    endTime: formatWallClockToTimeString(end),
    timeZoneId: tz,
    timeFormat: 'yyyy-MM-dd HH:mm:ss'
  });

  const handleTimeRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) {
      setTimeRange(null);
      onChange?.(undefined);
      return;
    }
    setTimeRange([dates[0], dates[1]]);
    if (pickDateOnly) {
      onChange?.(buildDateOnlyBillTimeRange(dates[0], dates[1], timeZone));
    } else {
      onChange?.(buildTimeRangeInput(dates[0], dates[1], timeZone));
    }
  };

  const handleTimeZoneChange = (newTimeZone: string) => {
    setTimeZone(newTimeZone);

    if (!timeRange?.[0] || !timeRange?.[1]) {
      return;
    }
    if (pickDateOnly) {
      const input = buildDateOnlyBillTimeRange(timeRange[0], timeRange[1], newTimeZone);
      onChange?.(input);
      setTimeRange([
        dayjs.tz(input.startTime, newTimeZone),
        dayjs.tz(input.endTime, newTimeZone)
      ]);
    } else {
      onChange?.(buildTimeRangeInput(timeRange[0], timeRange[1], newTimeZone));
    }
  };

  return (
    <div className="flex items-center gap-4 flex-wrap">
      <RangePicker
        showTime={
          pickDateOnly
            ? false
            : {
                defaultValue: [dayjs('00:00:00', 'HH:mm:ss'), dayjs('23:59:59', 'HH:mm:ss')],
                format: 'HH:mm:ss'
              }
        }
        format={pickDateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss'}
        placeholder={pickDateOnly ? ['开始日期', '结束日期'] : ['开始时间', '结束时间']}
        value={timeRange}
        onChange={handleTimeRangeChange}
      />

      <div className="flex items-center">
        <ClockCircleOutlined className="mr-2 text-gray-500" />
        <Select
          value={timeZone}
          onChange={handleTimeZoneChange}
          options={TIMEZONE_OPTIONS}
          className="w-64"
          placeholder="选择时区 (IANA)"
        />
      </div>
    </div>
  );
};
